import React, { useEffect, useState } from 'react';
import type {
  PromotionPreview as PromotionPreviewType,
  CreateSeasonRequest,
  Season,
  Division,
} from '@sulis-liiga/common';
import { DivisionStatus } from '@sulis-liiga/common';
import PromotionRelegationZone from '../league/PromotionRelegationZone';
import SeasonForm from '../league/SeasonForm';

type WizardStep = 'review' | 'finalize' | 'create-season' | 'done';

interface SeasonTransitionWizardProps {
  leagueId: string;
  seasonId: string;
  /** Seasons to offer as "borrow divisions from" source in the create-season step. */
  existingSeasons?: Season[];
  /** Divisions of the season being finalized — used to gate finalization. */
  divisions?: Division[];
  promotionPreview: PromotionPreviewType | null;
  loading: boolean;
  /** Called on mount (and on manual refresh) to load the season-scoped preview. */
  onFetchPreview: () => void;
  /** Called to finalize the season (archive standings, expire points, mark COMPLETED). */
  onFinalize: () => Promise<void>;
  /**
   * Called when the admin creates the next season in step 3. Resolves with the
   * created season (so the wizard can link to its management page) or rejects on
   * failure (so the wizard stays on the create step and surfaces the error).
   */
  onCreateSeason: (data: CreateSeasonRequest) => Promise<Season | undefined>;
  /** Navigate to the newly created season's management page (single source). */
  onManageSeason?: (newSeasonId: string) => void;
}

/**
 * SeasonTransitionWizard — season-based 4-step flow.
 *
 * 1. Review       — load + show the season-scoped promotion/relegation preview.
 * 2. Finalize     — confirm irreversible finalization (archive + expire + complete).
 * 3. Create       — optionally create the next season (SeasonForm in create mode).
 * 4. Done         — success summary.
 */
const SeasonTransitionWizard: React.FC<SeasonTransitionWizardProps> = ({
  leagueId,
  seasonId,
  existingSeasons = [],
  divisions = [],
  promotionPreview,
  loading,
  onFetchPreview,
  onFinalize,
  onCreateSeason,
  onManageSeason,
}) => {
  const [currentStep, setCurrentStep] = useState<WizardStep>('review');
  const [createLoading, setCreateLoading] = useState(false);
  const [newSeasonId, setNewSeasonId] = useState<string | null>(null);

  // Finalization is blocked until every division in the season is completed.
  const openDivisions = divisions.filter(d => d.status !== DivisionStatus.COMPLETED);
  const allDivisionsCompleted = openDivisions.length === 0;

  const steps: { key: WizardStep; label: string }[] = [
    { key: 'review', label: 'Review' },
    { key: 'finalize', label: 'Finalize' },
    { key: 'create-season', label: 'Create Season' },
    { key: 'done', label: 'Done' },
  ];

  const currentStepIndex = steps.findIndex(s => s.key === currentStep);

  // Auto-load the preview when the wizard mounts.
  useEffect(() => {
    onFetchPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFinalize = async () => {
    try {
      await onFinalize();
      setCurrentStep('create-season');
    } catch {
      // Error is surfaced via the page-level error banner; stay on finalize step.
    }
  };

  const handleCreateSeason = async (data: CreateSeasonRequest) => {
    setCreateLoading(true);
    try {
      const season = await onCreateSeason(data);
      setNewSeasonId(season?.id ?? null);
      setCurrentStep('done');
    } catch {
      // Error surfaced via page-level banner; stay on the create step.
    } finally {
      setCreateLoading(false);
    }
  };

  const handleSkipCreate = () => {
    setCurrentStep('done');
  };

  const handleStartOver = () => {
    setNewSeasonId(null);
    setCurrentStep('review');
    onFetchPreview();
  };

  return (
    <div className="bg-white shadow rounded-lg">
      {/* Step indicator */}
      <div className="px-6 py-4 border-b border-gray-200">
        <nav aria-label="Progress">
          <ol className="flex items-center">
            {steps.map((step, index) => (
              <li key={step.key} className={`relative ${index < steps.length - 1 ? 'flex-1' : ''}`}>
                <div className="flex items-center">
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                      index < currentStepIndex
                        ? 'bg-primary-600 text-white'
                        : index === currentStepIndex
                        ? 'border-2 border-primary-600 text-primary-600 bg-white'
                        : 'border-2 border-gray-300 text-gray-400 bg-white'
                    }`}
                  >
                    {index < currentStepIndex ? (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      index + 1
                    )}
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 ml-2 ${
                        index < currentStepIndex ? 'bg-primary-600' : 'bg-gray-300'
                      }`}
                    />
                  )}
                </div>
                <p className="mt-2 text-xs font-medium text-gray-600 hidden sm:block">
                  {step.label}
                </p>
              </li>
            ))}
          </ol>
        </nav>
      </div>

      {/* Step content */}
      <div className="p-6">
        {/* Step 1: Review */}
        {currentStep === 'review' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Promotion / Relegation Preview</h3>
              <button
                type="button"
                onClick={onFetchPreview}
                disabled={loading}
                className="text-sm text-primary-600 hover:underline disabled:opacity-50"
              >
                {loading ? 'Loading…' : 'Refresh'}
              </button>
            </div>

            {loading && !promotionPreview && (
              <div className="flex items-center justify-center py-8">
                <svg
                  className="animate-spin h-8 w-8 text-primary-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
            )}

            {!loading && !promotionPreview && (
              <div className="text-center py-8 text-gray-500">
                <p>Preview not yet loaded. Click Refresh to load.</p>
              </div>
            )}

            {promotionPreview && (
              <>
                {/* Summary */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">Summary</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>
                      <span className="font-medium">{promotionPreview.promotions.length}</span>{' '}
                      teams to be promoted
                    </li>
                    <li>
                      <span className="font-medium">{promotionPreview.relegations.length}</span>{' '}
                      teams to be relegated
                    </li>
                    {promotionPreview.teamsWithoutNextDivision.length > 0 && (
                      <li className="text-yellow-800">
                        <span className="font-medium">
                          {promotionPreview.teamsWithoutNextDivision.length}
                        </span>{' '}
                        teams without a target division
                      </li>
                    )}
                  </ul>
                </div>

                {/* Movement detail */}
                <PromotionRelegationZone preview={promotionPreview} />

                {promotionPreview.teamsWithoutNextDivision.length > 0 && (
                  <div className="rounded-md bg-yellow-50 p-4 border border-yellow-200">
                    <p className="text-sm text-yellow-800">
                      Some teams have no target division. You may need to create additional
                      divisions before finalizing.
                    </p>
                  </div>
                )}
              </>
            )}

            {!allDivisionsCompleted && (
              <div className="rounded-md bg-yellow-50 p-4 border border-yellow-200">
                <p className="text-sm font-medium text-yellow-800">
                  All divisions must be completed before this season can be finalized.
                </p>
                <p className="mt-1 text-sm text-yellow-700">
                  Still open: {openDivisions.map(d => d.name).join(', ')}. Mark each division
                  Completed from the league page, then return here.
                </p>
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setCurrentStep('finalize')}
                disabled={!promotionPreview || loading || !allDivisionsCompleted}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 disabled:opacity-50"
              >
                Next: Finalize Season
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Finalize */}
        {currentStep === 'finalize' && (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">Finalize Season</h3>

            <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-yellow-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-yellow-800">
                    This action is irreversible.
                  </p>
                  <p className="mt-1 text-sm text-yellow-700">
                    Finalizing the season will archive standings, expire all team points, and mark
                    the season as Completed. You cannot undo this.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setCurrentStep('review')}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleFinalize}
                disabled={loading || !allDivisionsCompleted}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-danger-600 border border-transparent rounded-md shadow-sm hover:bg-danger-700 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Finalizing…
                  </>
                ) : (
                  'Finalize Season'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Create upcoming season (optional) */}
        {currentStep === 'create-season' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Create Upcoming Season</h3>
              <button
                type="button"
                onClick={handleSkipCreate}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Skip this step
              </button>
            </div>

            <p className="text-sm text-gray-600">
              The finalized season is pre-selected as the division source. Returning teams are
              auto-placed into their promotion/relegation tiers as a draft you can adjust from
              Manage Season.
            </p>

            <SeasonForm
              onSubmit={data => void handleCreateSeason(data as CreateSeasonRequest)}
              onCancel={handleSkipCreate}
              loading={createLoading}
              isEdit={false}
              existingSeasons={existingSeasons}
              leagueId={leagueId}
              defaultBorrowSeasonId={seasonId}
            />
          </div>
        )}

        {/* Step 4: Done */}
        {currentStep === 'done' && (
          <div className="text-center py-8 space-y-4">
            <div className="flex justify-center">
              <svg
                className="h-12 w-12 text-green-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900">Season Transition Complete</h3>
            <p className="text-sm text-gray-600">
              The season has been finalized. Standings are archived and points have been expired.
              {newSeasonId
                ? ' Returning teams were auto-placed into their promotion/relegation tiers as a draft.'
                : ''}
            </p>
            {newSeasonId && (
              <p className="text-sm text-gray-500">
                Review and finalize placements from the new season&rsquo;s management page, then set
                each division active.
              </p>
            )}
            <div className="flex justify-center gap-3">
              {newSeasonId && onManageSeason && (
                <button
                  type="button"
                  onClick={() => onManageSeason(newSeasonId)}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700"
                >
                  Manage new season
                </button>
              )}
              <button
                type="button"
                onClick={handleStartOver}
                className={`px-4 py-2 text-sm font-medium rounded-md border shadow-sm ${
                  newSeasonId && onManageSeason
                    ? 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                    : 'text-white bg-primary-600 border-transparent hover:bg-primary-700'
                }`}
              >
                Start Over
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SeasonTransitionWizard;
